import React, { useState, useEffect, useRef } from 'react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { makeStyles, TextField, Typography } from '@material-ui/core'
import { debounce } from 'lodash'
import useSelectedFacility from '../../hooks/useSelectedFacility'
import ClientBlock from './components/ClientBlock'

const useStyles = makeStyles(() => ({
	householdSearch: {
		'& .MuiAutocomplete-inputRoot': {
			paddingTop: '8px'
		}
	}
}))

/**
 * Autocomplete for searching households with existing training enrollments
 * Just uses local component state rather than redux households
 * @param {{
 * 		onHouseholdSelected: Function,
 * 		displayAddress: Boolean,
 * 		additionalSearchParams: Array,
 * 		excludePetIds: Set
 * }} param0
 * @returns
 */
const TrainingClassEnrollmentsAutocomplete = ({
	onHouseholdSelected,
	displayAddress = false,
	additionalSearchParams = [],
	excludePetIds = new Set([])
}) => {
	const classes = useStyles()

	const { facility } = useSelectedFacility()
	const { getHouseholdsWithTrainingEnrollments } = useSelectedFacility()
	const [households, setHouseholds] = useState([])
	const [household, setHousehold] = useState(null)
	const [loading, setLoading] = useState(false)

	const [open, setOpen] = React.useState(false)
	const [search, setSearch] = useState('')

	const delayedSetSearch = useRef(debounce((t) => setSearch(t), 750)).current

	const updateSearch = (value) => delayedSetSearch(value)

	useEffect(() => {
		const get = async () => {
			if (search.length < 3) {
				return
			}
			setLoading(true)
			const { success, data } = await getHouseholdsWithTrainingEnrollments({
				facility_id: facility._id,
				search,
				additionalSearchParams
			})
			setLoading(false)
			if (success) {
				const filtered = []

				for (const household of data) {
					const { pets } = household
					const filteredPets = pets.filter((pet) => !excludePetIds.has(pet._id))
					if (filteredPets.length > 0) {
						filtered.push({ ...household, pets: filteredPets })
					}
				}
				setHouseholds(filtered)
			}
		}

		if (search && facility) {
			get()
		}
	}, [search, facility, setLoading, setHouseholds, additionalSearchParams, excludePetIds])

	useEffect(() => {
		if (!open) {
			setHouseholds([])
			setSearch('')
		}
	}, [open])

	const handleHouseholdSelected = (household) => {
		setHousehold(household)
		if (onHouseholdSelected) {
			onHouseholdSelected(household)
		}
	}

	const clearHousehold = () => {
		handleHouseholdSelected(null)
		setHouseholds([])
	}

	return !household ? (
		<Autocomplete
			id="household-enrollments-select"
			getOptionLabel={(option) =>
				typeof option === 'string'
					? option?.household?.account?.full_name
					: option.household?.account.full_name || 'No Name'
			}
			filterOptions={(x) => x}
			options={households}
			className={classes.householdSearch}
			autoComplete
			noOptionsText="No clients match for this course."
			loading={loading}
			includeInputInList
			disableOpenOnFocus
			open={open}
			onOpen={() => {
				setOpen(true)
			}}
			onClose={() => {
				setOpen(false)
			}}
			onChange={(event, newValue) => {
				handleHouseholdSelected(newValue)
			}}
			renderOption={(option) => (
				<React.Fragment>
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<Typography variant="body2">{option?.household?.account?.full_name}</Typography>
						<Typography style={{ fontSize: '10px' }}>{option?.household?.account?.email}</Typography>
					</div>
				</React.Fragment>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Search existing clients"
					variant="filled"
					fullWidth
					onChange={(e) => updateSearch(e.target.value)}
				/>
			)}
		/>
	) : (
		<ClientBlock
			household={household?.household}
			onClearHousehold={clearHousehold}
			displayAddress={displayAddress}
		/>
	)
}

export default TrainingClassEnrollmentsAutocomplete
